import { useEffect, useState } from 'react';
import './labDial.css';

/* The public lab's last 24 hours on a clock face. Midnight is at
   the top, the hand is the reader's local time, and each mark is one event
   at the time it happened. Cyan marks are actions Vigilhold took. */
const LABELS = {
  ssh_auth_fail: 'Failed SSH logins',
  port_scan: 'Port scan',
  process_flagged: 'Process flagged',
  file_change: 'File changed',
  privilege: 'Privilege change',
  anomaly: 'Unusual change',
  ip_banned: 'Address banned',
  ip_throttled: 'Address slowed down',
};
const HANDLED = new Set(['ip_banned', 'ip_throttled']);
const DAY = 24 * 3600_000;
const C = 160, R = 132;

const angleOf = d => ((d.getHours() * 60 + d.getMinutes()) / 1440) * 2 * Math.PI;
const at = (a, r) => [C + r * Math.sin(a), C - r * Math.cos(a)];
const clock = d => `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${d.getHours() < 12 ? 'a.m.' : 'p.m.'}`;

export default function LabDial({ fig }) {
  const [lab, setLab] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let live = true;
    const load = () => fetch('/api/lab')
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => { if (live) { setLab(d); setNow(new Date()); } })
      .catch(() => {});
    load();
    const id = setInterval(load, 120000);
    return () => { live = false; clearInterval(id); };
  }, []);

  if (!lab) return null;
  const recent = lab.events.filter(e => now - e.ts < DAY);
  if (!recent.length) return null;

  const handled = recent.filter(e => HANDLED.has(e.type)).length;
  const hand = angleOf(now);
  const [hx, hy] = at(hand, R - 22);

  return (
    <figure className="dusk-plate lab-dial">
      <svg viewBox="0 0 320 320" role="img" aria-label={`Clock face of the last 24 hours on the lab: ${recent.length} events, ${handled} handled by Vigilhold.`}>
        <circle cx={C} cy={C} r={R} className="dial-ring" />
        {Array.from({ length: 48 }, (_, i) => {
          const a = (i / 48) * 2 * Math.PI, long = i % 12 === 0;
          const [x1, y1] = at(a, R + 4), [x2, y2] = at(a, R + (long ? 16 : 9));
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} className={long ? 'dial-tick long' : 'dial-tick'} />;
        })}
        {['12 a.m.', '6 a.m.', '12 p.m.', '6 p.m.'].map((t, i) => {
          const [x, y] = at((i / 4) * 2 * Math.PI, R - 62);
          return <text key={t} x={x} y={y} className="dial-hour" textAnchor="middle" dominantBaseline="middle">{t}</text>;
        })}
        {recent.map((e, i) => {
          const a = angleOf(new Date(e.ts)), h = HANDLED.has(e.type);
          const [x1, y1] = at(a, R - (h ? 2 : 0)), [x2, y2] = at(a, R - (h ? 44 : 30));
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} className={h ? 'dial-ev handled' : 'dial-ev'} />;
        })}
        <line x1={C} y1={C} x2={hx} y2={hy} className="dial-hand" />
        <circle cx={C} cy={C} r="3.5" className="dial-hub" />
      </svg>

      <div className="dial-key">
        <p className="label dial-live"><span className="lamp" aria-hidden="true" /> Live from the lab, last 24 hours</p>
        <dl className="dial-counts">
          <div><dt>Latest events</dt><dd>{recent.length}</dd></div>
          <div className="handled"><dt>Handled on its own</dt><dd>{handled}</dd></div>
        </dl>
        <ol className="dial-recent" aria-label="Latest events">
          {recent.slice(0, 4).map((e, i) => (
            <li key={i} className={HANDLED.has(e.type) ? 'handled' : undefined}>
              <span className="label">{clock(new Date(e.ts))}</span>
              {LABELS[e.type] || 'Event'}{e.count > 1 ? ` ×${e.count}` : ''}
            </li>
          ))}
        </ol>
      </div>
      <figcaption className="fig-cap">
        <b>Fig. {fig}</b> — The lab’s last day on a clock, in your local time. Each mark is an event, and failed logins are grouped into bursts, so one mark can be dozens of attempts. Cyan marks are actions Vigilhold took by itself.{lab.mode && ` The lab runs in ${lab.mode}.`}
      </figcaption>
    </figure>
  );
}
